"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const lodash_1 = __importDefault(require("lodash"));
const TextComponent_1 = __importDefault(require("./TextComponent"));
const TextWidget_1 = __importDefault(require("./TextWidget"));
const AsyncLoadComponent_1 = __importDefault(require("@mwater/react-library/lib/AsyncLoadComponent"));
const DropdownWidgetComponent_1 = __importDefault(require("../DropdownWidgetComponent"));
const ActionCancelModalComponent_1 = __importDefault(require("@mwater/react-library/lib/ActionCancelModalComponent"));
const bootstrap_1 = require("@mwater/react-library/lib/bootstrap");
const ColorComponent_1 = __importDefault(require("../../ColorComponent"));
const bootstrap_2 = require("@mwater/react-library/lib/bootstrap");
const ExprItemsTranslator_1 = require("../../richtext/ExprItemsTranslator");
// Widget which displays styled text with embedded expressions
class TextWidgetComponent extends AsyncLoadComponent_1.default {
    divComp;
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            // Map of expression id to expression value
            exprValues: {},
            error: null,
            cacheExpiry: props.dataSource.getCacheExpiry(), // Save cache expiry to see if changes
            editModalOpen: false
        };
    }
    // Override to determine if a load is needed. Not called on mounting
    isLoadNeeded(newProps, oldProps) {
        // Get expression items recursively
        function getExprItems(items) {
            let exprItems = [];
            for (let item of items || []) {
                if (typeof item === "object") {
                    if (item.type === "expr") {
                        exprItems.push(item);
                    }
                    if (item.items) {
                        exprItems = exprItems.concat(getExprItems(item.items));
                    }
                }
            }
            return exprItems;
        }
        // Reload if filters or expressions have changed or cache expiry
        return (!lodash_1.default.isEqual(newProps.filters, oldProps.filters) ||
            !lodash_1.default.isEqual(getExprItems(newProps.design.items), getExprItems(oldProps.design.items)) ||
            newProps.dataSource.getCacheExpiry() !== this.state.cacheExpiry ||
            newProps.refreshKey !== oldProps.refreshKey);
    }
    // Call callback with state changes
    load(props, prevProps, callback) {
        // Shortcut if no expressions in text widget
        const widget = new TextWidget_1.default();
        if (widget.getExprItems(props.design.items).length === 0) {
            callback({ error: null, exprValues: {} }, props.dataSource.getCacheExpiry());
            return;
        }
        // Get data
        props.widgetDataSource.getData(props.design, props.filters, (error, data) => {
            callback({ error, exprValues: data || {}, cacheExpiry: props.dataSource.getCacheExpiry() });
        });
    }
    scrollToTOCEntry(entryId) {
        // Find entry in divComp
        const entries = this.divComp.querySelectorAll("h1,h2,h3,h4,h5,h6,h7,h8,h9");
        const entry = entries[entryId];
        if (entry) {
            return entry.scrollIntoView(true);
        }
    }
    handleEditClick = () => {
        this.setState({ editModalOpen: true });
    };
    handleEditModalClose = () => {
        this.setState({ editModalOpen: false });
    };
    handleBackgroundColorChange = (color) => {
        const newDesign = { ...this.props.design, backgroundColor: color };
        this.props.onDesignChange(newDesign);
    };
    handleSpacingChange = (spacing) => {
        const newDesign = { ...this.props.design, padding: spacing ?? undefined };
        this.props.onDesignChange(newDesign);
    };
    handleBorderColorChange = (color) => {
        const newDesign = { ...this.props.design, borderColor: color };
        this.props.onDesignChange(newDesign);
    };
    handleBorderThicknessChange = (thickness) => {
        const newDesign = { ...this.props.design, borderThickness: thickness };
        this.props.onDesignChange(newDesign);
    };
    handleBorderRadiusChange = (radius) => {
        const newDesign = { ...this.props.design, borderRadius: radius };
        this.props.onDesignChange(newDesign);
    };
    renderEditModal() {
        if (!this.state.editModalOpen) {
            return null;
        }
        const spacingOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40].map(value => ({ value, label: `${value}px` }));
        const borderThicknessOptions = [0, 1, 2, 3, 4, 5].map(value => ({ value, label: value === 0 ? T `None` : `${value}px` }));
        return (react_1.default.createElement(ActionCancelModalComponent_1.default, { title: T `Edit Text Widget`, onCancel: this.handleEditModalClose, onAction: this.handleEditModalClose },
            react_1.default.createElement(bootstrap_1.FormGroup, { label: T `Background Color` },
                react_1.default.createElement(ColorComponent_1.default, { color: this.props.design.backgroundColor || null, onChange: this.handleBackgroundColorChange }),
                react_1.default.createElement("div", { className: "text-muted" }, T `Choose a background color for the text widget`)),
            react_1.default.createElement(bootstrap_1.FormGroup, { label: T `Padding` },
                react_1.default.createElement(bootstrap_2.Select, { value: this.props.design.padding ?? 0, onChange: this.handleSpacingChange, options: spacingOptions }),
                react_1.default.createElement("div", { className: "text-muted" }, T `Choose extra padding for the text widget`)),
            react_1.default.createElement(bootstrap_1.FormGroup, { label: T `Border Thickness`, help: T `Choose the border thickness for the text widget` },
                react_1.default.createElement(bootstrap_2.Select, { value: this.props.design.borderThickness ?? 0, onChange: this.handleBorderThicknessChange, options: borderThicknessOptions })),
            this.props.design.borderThickness &&
                react_1.default.createElement(bootstrap_1.FormGroup, { label: T `Border Color`, help: T `Choose the border color for the text widget` },
                    react_1.default.createElement(ColorComponent_1.default, { color: this.props.design.borderColor || null, onChange: this.handleBorderColorChange })),
            this.props.design.borderThickness &&
                react_1.default.createElement(bootstrap_1.FormGroup, { label: T `Border Radius`, help: T `Choose the border radius for the text widget` },
                    react_1.default.createElement(bootstrap_2.Select, { value: this.props.design.borderRadius ?? 0, onChange: this.handleBorderRadiusChange, options: spacingOptions }))));
    }
    renderTextComponent() {
        // If loading, don't display old values
        const exprValues = !this.state.loading ? this.state.exprValues : {};
        const padding = this.props.design.padding ?? 0;
        // Translate items if needed
        const translatedDesign = this.props.onDesignChange ? this.props.design : {
            ...this.props.design,
            items: (0, ExprItemsTranslator_1.translateHtmlItems)(this.props.design.items || [], this.props.translate)
        };
        return (react_1.default.createElement(TextComponent_1.default, { design: translatedDesign, onDesignChange: this.props.onDesignChange, schema: this.props.schema, dataSource: this.props.dataSource, exprValues: exprValues, width: this.props.width ? this.props.width - padding * 2 : undefined, height: this.props.height ? this.props.height - padding * 2 : undefined, singleRowTable: this.props.singleRowTable, namedStrings: this.props.namedStrings, locale: this.props.locale }));
    }
    render() {
        const borderStyle = this.props.design.borderThickness && this.props.design.borderColor
            ? `${this.props.design.borderThickness}px solid ${this.props.design.borderColor}`
            : undefined;
        return (react_1.default.createElement("div", { style: {
                backgroundColor: this.props.design.backgroundColor ?? undefined,
                padding: this.props.design.padding != null ? `${this.props.design.padding}px` : undefined,
                border: borderStyle,
                borderRadius: this.props.design.borderRadius != null ? `${this.props.design.borderRadius}px` : undefined
            }, ref: comp => this.divComp = comp },
            this.props.onDesignChange ? (react_1.default.createElement(DropdownWidgetComponent_1.default, { dropdownItems: [{
                        label: T `Edit`,
                        onClick: this.handleEditClick
                    }] }, this.renderTextComponent())) : (this.renderTextComponent()),
            this.renderEditModal()));
    }
}
exports.default = TextWidgetComponent;
